import * as React from "react";
import {useEffect, useMemo, useRef, useState} from "react";
import {KatButton, KatDataTable} from "@amzn/katal-react";
import {CaseResult} from "src/app/model/lighthouse/ManagementModels";
import {buildCaseActivityTableHeader} from "src/app/config/ManagementTableConfig";

interface CaseActivityTableProps {
    cases: CaseResult[];          // current page of results (from the Search API)
    onAction: (action: string, caseId: string) => void;
    onBulkAction: () => void;     // "Bulk Action" action (top-right of the table header)
    canBulkAction: boolean;       // Bulk Action is Manager-only (server-driven capability)
    maxSelection?: number;        // Max cases selectable for bulk action (default 1000)
    showLogin: boolean;           // show the auditor Login column (QA/Manager only)
    // Selection is owned by the parent so it persists across page navigation (the table
    // unmounts during the loading spinner on each page change).
    selectedIds: Set<string>;
    onToggleSelect: (caseId: string) => void;
    onToggleAll: () => void;      // toggles all rows on the current page
    onNext: () => void;
    onPrevious: () => void;
    canNext: boolean;
    canPrevious: boolean;
}

/** Shape of the KatDataTable column object we care about when sorting. */
interface SortableColumn {
    sortValue?: (r: CaseResult) => string | number | undefined;
}

/**
 * Renders the current page of the Case Activity table. Fully controlled: the parent owns the
 * data + pagination (offset/limit + nextToken) and the row selection. Sorting is client-side.
 */
const CaseActivityTable = (props: CaseActivityTableProps) => {
    const scrollRef = useRef<HTMLDivElement>(null);

    const [sortColumn, setSortColumn] = useState<SortableColumn | null>(null);
    const [sortDirection, setSortDirection] = useState<'asc' | 'desc'>('asc');

    // Responsive height: fill from the table's top down to just above the pagination bar +
    // footer. Recomputed on window resize (which also covers filter-row re-wrapping), so the
    // table always covers exactly the available screen space — never a fixed size.
    const [scrollHeight, setScrollHeight] = useState<number | undefined>(undefined);
    useEffect(() => {
        // Pagination bar (~40px) + a small bottom margin. The page renders standalone
        // (no portal footer), so no footer space is reserved.
        const RESERVED_BELOW = 50;
        const update = () => {
            const el = scrollRef.current;
            if (!el) return;
            const top = el.getBoundingClientRect().top;
            setScrollHeight(Math.max(200, window.innerHeight - top - RESERVED_BELOW));
        };
        update();
        window.addEventListener('resize', update);
        return () => window.removeEventListener('resize', update);
    }, []);

    const onSortColumnChanged = (e: any) => {
        setSortColumn(e?.detail?.column ?? null);
        setSortDirection(e?.detail?.sortDirection === 'desc' ? 'desc' : 'asc');
    };

    const sortedCases = useMemo(() => {
        const accessor = sortColumn?.sortValue;
        if (!accessor) return props.cases;
        const dir = sortDirection === 'desc' ? -1 : 1;
        return [...props.cases].sort((a, b) => {
            const av = accessor(a);
            const bv = accessor(b);
            if (av == null && bv == null) return 0;
            if (av == null) return -1 * dir;
            if (bv == null) return 1 * dir;
            if (typeof av === 'number' && typeof bv === 'number') return (av - bv) * dir;
            return String(av).localeCompare(String(bv)) * dir;
        });
    }, [props.cases, sortColumn, sortDirection]);

    const columns = useMemo(
        () => {
            if (!props.canBulkAction) return buildCaseActivityTableHeader(props.onAction, undefined, props.showLogin);
            const pageIds = sortedCases.map(c => c.caseId);
            const allSelected = pageIds.length > 0 && pageIds.every(id => props.selectedIds.has(id));
            return buildCaseActivityTableHeader(props.onAction, {
                isSelected: (id: string) => props.selectedIds.has(id),
                onToggle: props.onToggleSelect,
                allSelected,
                onToggleAll: props.onToggleAll
            }, props.showLogin);
        },
        [props.onAction, props.canBulkAction, props.showLogin, props.selectedIds, props.onToggleSelect, props.onToggleAll, sortedCases]);

    const maxSelection = props.maxSelection ?? 1000;

    return (
        <div data-testid='case-activity-table'>
            <div className='case-activity-header'>
                <span className='case-activity-title'>Case Activity</span>
                <div className='case-activity-actions'>
                    {props.canBulkAction && props.selectedIds.size > 0 && (
                        props.selectedIds.size > maxSelection ? (
                            <span className='selected-count' data-testid='selected-count'
                                  style={{color: '#d13212'}}>
                                {props.selectedIds.size} selected — max {maxSelection} allowed
                            </span>
                        ) : (
                            <span className='selected-count' data-testid='selected-count'>
                                {props.selectedIds.size} selected
                            </span>
                        )
                    )}
                    {props.canBulkAction && (
                        <KatButton variant='primary' onClick={props.onBulkAction}
                                   disabled={props.selectedIds.size === 0 || props.selectedIds.size > maxSelection}
                                   data-testid='bulk-action-btn'>Bulk Action</KatButton>
                    )}
                </div>
            </div>
            <div ref={scrollRef} className="kat default-margin case-table-scroll"
                 style={{height: scrollHeight}}>
                <KatDataTable columns={columns} rowData={sortedCases}
                            className='data-table'
                              onSortColumnChanged={onSortColumnChanged}
                              data-testid='case-table'></KatDataTable>
            </div>

            <div className='case-pagination-bar' data-testid='case-pagination'>
                <div/>

                <div className='case-pagination-center'>
                    <KatButton variant='secondary' size='small' onClick={props.onPrevious}
                               disabled={!props.canPrevious} data-testid='page-previous'>
                        Previous
                    </KatButton>
                    <KatButton variant='secondary' size='small' onClick={props.onNext}
                               disabled={!props.canNext} data-testid='page-next'>
                        Next
                    </KatButton>
                </div>

                <div/>
            </div>
        </div>
    );
};

export default CaseActivityTable;
